import {
  BaseQueryApi,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "../../utils/utils";
import i18n from "../../i18n";
import {
  IInsertAllAnswersResponse,
  IUser,
  IValidateNumberResponse,
  IVerificationData,
  TGetQuestionByIdResponse,
  TInsertAllAnswersRequest,
  TInsertAllAnswersResponse,
  TUploadResponse,
  TInsertAnswerRequest,
  TQuestion,
  TReportData,
  TValidateNumberRequest,
} from "../types";

export let BASE_URL_DYNAMIC = BASE_URL;

const loadConfig = async () => {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();
    BASE_URL_DYNAMIC =
      (window.location.hostname.includes("test") ||
      window.location.hostname.includes("local")
        ? config.serverUrlTest
        : config.serverUrl) || BASE_URL;
  } catch (error) {
    console.error("Error loading config:", error);
  }
};

// This ensures that BASE_URL is loaded before you initialize your API
await loadConfig();

export const api = createApi({
  reducerPath: "utils",
  tagTypes: ["api", "language", "Conversations", "ConversationHistory"],
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL_DYNAMIC,
    // Send the bpreven.auth HttpOnly cookie on cross-origin API calls and
    // honor Set-Cookie from the login response. Required for FileProxy media
    // access via the cookie auth scheme. Safe: CORS allow-list is explicit
    // (no wildcard) and the cookie scheme is only accepted on the read-only
    // FileProxyController.
    credentials: "include",
    prepareHeaders: (
      headers,
      endpoints:
        | undefined
        | Pick<
            BaseQueryApi,
            "type" | "getState" | "extra" | "endpoint" | "forced"
          >
    ) => {
      if (
        !headers.has("Content-Type") &&
        endpoints?.endpoint !== "insertAudioAnswersQuestions" &&
        endpoints?.endpoint !== "postFiles" &&
        endpoints?.endpoint !== "getTranscription"
      ) {
        headers.set("Content-Type", "application/json");
      }
      const lang =
        (typeof i18n?.language === "string" && i18n.language) ||
        localStorage.getItem("i18nextLng") ||
        navigator.language ||
        "en";
      headers.set("Accept-Language", lang);
      headers.set("X-App-Name", "poc");
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCurrentQuestionnaireByName: build.query<
      TReportData,
      { name?: string; symbol?: string }
    >({
      query: ({ name = "", symbol }) => ({
        url: `Questions/GetCurrentQuestionnaire/${name}${
          symbol ? `/${symbol}` : ""
        }`,
        method: "GET",
      }),
    }),
    getCurrentQuestionnaireByGuid: build.query<
      TReportData,
      {
        guid: string;
        questionnaireCompaniesTypeId: number | string;
        isForTHerapist?: boolean;
      }
    >({
      query: ({
        guid,
        questionnaireCompaniesTypeId,
        isForTHerapist = false,
      }) => ({
        url: `Questions/GetCurrentQuestionnaireByType/${guid}/${questionnaireCompaniesTypeId}${
          isForTHerapist ? "/" + isForTHerapist : ""
        }`,
        method: "GET",
      }),
    }),
    /**
     * One specific questionnaire component for one patient.
     *
     * The sibling of getCurrentQuestionnaireByGuid, which asks for "the questionnaire of type X
     * under this patient's own header". A treatment protocol can point at a different header, so
     * its links name the component outright and pass the session the answers belong to.

     */
    getQuestionnaireByComponent: build.query<
      TReportData,
      {
        guid: string;
        questionnaireCompaniesId: number | string;
        appointmentId?: number | string | null;
      }
    >({
      query: ({ guid, questionnaireCompaniesId, appointmentId }) => ({
        url: `Questions/GetQuestionnaireByComponent/${guid}/${questionnaireCompaniesId}/${
          appointmentId ?? 0
        }`,

        method: "GET",
      }),
    }),
    getQuestionById: build.query<
      TGetQuestionByIdResponse,
      {
        nextQuestionId: number;
        skip?: boolean;
        questionnaireId: string | number;
      }
    >({
      query: ({ nextQuestionId, questionnaireId }) => ({
        url: `Questions/GetQuestionByQuestionnaireId/${questionnaireId}/${nextQuestionId}/0`,
        method: "GET",
      }),
    }),
    insertAnswerQuestion: build.mutation<
      TGetQuestionByIdResponse,
      TInsertAnswerRequest
    >({
      query: (option) => ({
        url: `/Questions/InsertAnswerQuestion`,
        method: "POST",
        body: JSON.stringify(option),
      }),
    }),
    insertAllAnswersQuestions: build.mutation<
      IInsertAllAnswersResponse,
      {
        answers: TInsertAllAnswersRequest[];
        companyId: number;
        questionnaireCompaniesId?: number;
        therapistGuid?: string | null;
        usersGuid?: string | null;
        fakeCompanyId?: number | null;
        /**
         * The session these answers belong to, when a treatment protocol asked for them. The
         * server checks the appointment is actually this patient's before stamping it, so a
         * hand-edited URL cannot attach answers to someone else's session.
         */
        appointmentId?: number | string | null;
      }
    >({
      query: ({
        answers,
        companyId,
        therapistGuid,
        usersGuid,
        questionnaireCompaniesId = 1,
        fakeCompanyId = null,
        appointmentId = null,
      }) => ({
        url:
          `/Questions/InsertAllAnswersQuestionsAnonymous/${companyId}/${questionnaireCompaniesId}/${usersGuid ?? "0"}/${therapistGuid ?? "0"}/${fakeCompanyId ?? 0}` +
          (appointmentId ? `?appointmentId=${appointmentId}` : ""),
        method: "POST",
        body: JSON.stringify(answers),
      }),
    }),
    getUserByPhoneNumber: build.query<
      {guid: string} | IVerificationData,
      { phone: string; companyId?: number | null; questionnaireId?: number }
    >({
      query: ({ phone, companyId = -1, questionnaireId }) => ({
        url: `user/User_GetUserByPhone?Phone=${encodeURIComponent(phone)}${
          companyId !== undefined && companyId !== null
            ? `&CompanyId=${companyId}`
            : ""
        }${
          questionnaireId !== undefined && questionnaireId !== null
            ? `&QuestionnaireId=${questionnaireId}`
            : ""
        }`,
        method: "GET",
      }),
    }),
    validateNumber: build.mutation<
      IValidateNumberResponse,
      { Type: number; CompanyId: number | string; body: TValidateNumberRequest }
    >({
      query: ({ Type, CompanyId, body }) => ({
        url: `user/User_ValidateNewUser/${Type}/${CompanyId}`,
        method: "POST",
        body: body,
      }),
    }),
    insertAudioAnswersQuestions: build.mutation<
      TInsertAllAnswersResponse,
      {
        answers: { questionId: number; file: Blob }[];
        companyId: number;
        questionnaireCompaniesScoreUsersId: number;
        guid: string | null;
      }
    >({
      query: ({
        answers,
        companyId,
        questionnaireCompaniesScoreUsersId,
        guid,
      }) => {
        const formData = new FormData();

        // Append multiple files to FormData
        answers.forEach((answer) => {
          const { file } = answer;
          const fileName = `${questionnaireCompaniesScoreUsersId}_${answer.questionId}.opus`;
          const fileWithProperName = new File([file], fileName, {
            type: file.type,
          });
          formData.append(
            "questionnaireCompaniesScoreUsersId",
            questionnaireCompaniesScoreUsersId.toString()
          );
          formData.append("files", fileWithProperName);
        });

        return {
          url: `/files/InsertQuestionnaireAudioAnswers/${companyId}/${guid}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    postFiles: build.mutation<
      TUploadResponse,
      {
        files: { file: File; questionId: number }[];
        questionnaireCompaniesScoreUsersId: number;
        userId: string | null;
      }
    >({
      query: ({
        files,
        questionnaireCompaniesScoreUsersId,
        userId,
      }) => {
        const formData = new FormData();
        // Append multiple files to FormData
        files.forEach(({ file, questionId }) => {
          const extension = file.name.split(".").pop();
          const fileName = `${questionnaireCompaniesScoreUsersId}_${questionId}.${extension}`;
          const renamedFile = new File([file], fileName, {
            type: file.type,
          });
          formData.append("files", renamedFile);
          formData.append(
            "questionnaireCompaniesScoreUsersId",
            questionnaireCompaniesScoreUsersId.toString()
          );
        });

        return {
          url: `files/UploadQuestionnaireFiles/${userId}`,
          method: "POST",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useLazyGetQuestionByIdQuery,
  useInsertAllAnswersQuestionsMutation,
  useLazyGetUserByPhoneNumberQuery,
  useLazyGetCurrentQuestionnaireByNameQuery,
  useLazyGetCurrentQuestionnaireByGuidQuery,
  useLazyGetQuestionnaireByComponentQuery,
  useValidateNumberMutation,
  useInsertAudioAnswersQuestionsMutation,
  usePostFilesMutation,
} = api;
